import React, { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams, useParams, Link } from "react-router-dom";
import {
  api, businessLoanCost, formatApiError, formatGBP, formatGBPCompact, formatGBP2,
  amountToPos, posToAmount, SLIDER_STEPS, AMOUNT_MIN, AMOUNT_MAX,
  DEFAULT_INDICATIVE_APR,
} from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Check, Shield, CheckCircle2, Upload, Trash2, Bookmark, Mail,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSeo from "@/hooks/useSeo";
import { useExitIntent } from "@/hooks/useExitIntent";

const PURPOSES = [
  "Working Capital", "Equipment / Asset Finance", "Business Expansion",
  "VAT / Tax Bill", "Invoice Finance", "Commercial Mortgage",
  "Property Development", "Stock Purchase", "Cash Flow",
  "Refinancing", "Management Buy-Out", "R&D / Innovation",
  "Marketing / Growth", "Vehicle Finance", "Acquisition Finance",
  "Bridging Finance", "Other",
];
const TITLES = ["Mr", "Mrs", "Ms", "Miss", "Dr", "Mx"];
const SECTORS = [
  "Retail", "Hospitality", "Construction", "Manufacturing",
  "Professional Services", "Healthcare", "Technology",
  "Transport & Logistics", "Property", "Wholesale", "Agriculture", "Other",
];
const TRADING_YEARS = ["< 1 year", "1–2 years", "2–5 years", "5–10 years", "10+ years"];
const ROLES = ["Director", "Owner", "CFO", "Finance Manager", "Other"];

const STEPS = [
  { id: 1, label: "Funding" },
  { id: 2, label: "Business" },
  { id: 3, label: "Financials" },
  { id: 4, label: "Contact" },
  { id: 5, label: "Review" },
];

const clampAmount = (v) => Math.max(AMOUNT_MIN, Math.min(AMOUNT_MAX, Number(v) || AMOUNT_MIN));

export default function Apply() {
  useSeo({
    title: "Apply for Business Finance — SwiftCash",
    description: "Get a tailored UK business finance quote in minutes. £5,000 to £500,000,000. 17 funding purposes. Soft eligibility check only — no impact on the business credit file.",
    path: "/apply",
  });
  const [sp] = useSearchParams();
  const { token: resumeToken } = useParams();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveEmail, setSaveEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveSent, setSaveSent] = useState(false);
  const [hydrating, setHydrating] = useState(!!resumeToken);
  const formTopRef = useRef(null);

  // Scrolls the form column into view, accounting for the sticky navbar (72px) + progress strip (~66px).
  // Uses smooth on desktop, instant on mobile to avoid a jarring scroll while the on-screen keyboard is open.
  const scrollFormIntoView = () => {
    if (typeof window === "undefined") return;
    const el = formTopRef.current;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    // Sticky header (72) + progress strip (~66) + small gap
    const OFFSET = isMobile ? 120 : 156;
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - OFFSET;
      window.scrollTo({ top: Math.max(0, y), behavior: isMobile ? "auto" : "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: isMobile ? "auto" : "smooth" });
    }
  };

  const [form, setForm] = useState({
    amount: clampAmount(sp.get("amount") || 250_000),
    term_months: Math.min(120, Math.max(1, Number(sp.get("term")) || 36)),
    purpose: "Working Capital",
    // Business
    company_name: "",
    company_number: "",
    sector: "Professional Services",
    trading_years: "2–5 years",
    business_postcode: "",
    // Financials
    annual_turnover: "",
    monthly_revenue: "",
    existing_debt: "",
    business_bank: "",
    // Contact
    title: "Mr",
    first_name: "",
    last_name: "",
    role: "Director",
    email: "",
    phone: "",
    // Consent
    marketing_consent: false,
    credit_check_consent: true,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [pendingDocs, setPendingDocs] = useState([]);  // File objects, uploaded after submit
  const { monthly, total } = useMemo(
    () => businessLoanCost(form.amount, form.term_months),
    [form.amount, form.term_months]
  );

  // Hydrate from a saved-draft magic link (/apply/resume/:token)
  const nav = useNavigate();
  useEffect(() => {
    if (!resumeToken) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/applications/draft/${resumeToken}`);
        if (!alive) return;
        if (data?.form && typeof data.form === "object") {
          setForm((f) => ({ ...f, ...data.form }));
          if (data.email && !data.form.email) setForm((f) => ({ ...f, email: data.email }));
          toast.success("Welcome back — your application is right where you left it.");
        }
      } catch (e) {
        toast.error(formatApiError(e) || "This resume link is no longer valid.");
        nav("/apply", { replace: true });
      } finally {
        if (alive) setHydrating(false);
      }
    })();
    return () => { alive = false; };
  }, [resumeToken, nav]);

  const openSaveDraft = () => {
    // Prefill save email if user has entered their contact email
    if (form.email && !saveEmail) setSaveEmail(form.email);
    setSaveOpen(true);
  };
  const saveDraft = async () => {
    if (!/^\S+@\S+\.\S+$/.test(saveEmail)) {
      toast.error("Please add a valid email so we can send you the resume link.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/applications/save-draft", { email: saveEmail, form });
      setSaveSent(true);
      toast.success("Sent — check your inbox for the resume link.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };
  const closeSaveDraft = () => { setSaveOpen(false); setSaveSent(false); };

  // Exit-intent: if the applicant has meaningfully progressed and moves the cursor
  // toward the top edge (about to close/switch tab), prompt them to save.
  const hasMeaningfulProgress = (
    step > 1
    || !!form.company_name
    || !!form.first_name
    || !!form.email
    || (form.annual_turnover !== "" && form.annual_turnover !== 0)
  );
  useExitIntent({
    enabled: !success && !saveOpen && hasMeaningfulProgress,
    onExitIntent: () => {
      openSaveDraft();
      toast.info("Before you go — save your progress and finish later?");
    },
  });

  const validateStep = () => {
    if (step === 1 && !form.purpose) return "Pick what the funding is for.";
    if (step === 2) {
      if (!form.company_name) return "Please add your company name.";
      if (!form.business_postcode) return "Please add the business postcode.";
    }
    if (step === 3) {
      if (form.annual_turnover === "" || form.annual_turnover === undefined) return "Please add your annual turnover.";
      if (form.monthly_revenue === "" || form.monthly_revenue === undefined) return "Please add your monthly revenue.";
      if (form.existing_debt === "" || form.existing_debt === undefined) return "Please add your existing debt (enter 0 if none).";
      if (Number(form.annual_turnover) < 0 || Number(form.monthly_revenue) < 0 || Number(form.existing_debt) < 0) return "Values can't be negative.";
    }
    if (step === 4) {
      if (!form.first_name || !form.last_name || !form.email || !form.phone) return "Please complete all contact fields.";
      if (!/^\S+@\S+\.\S+$/.test(form.email)) return "That email doesn't look right.";
    }
    return null;
  };

  const next = () => {
    const err = validateStep(); if (err) { toast.error(err); return; }
    setStep((s) => Math.min(STEPS.length, s + 1));
    // Wait a tick so the new step content mounts before we measure
    setTimeout(scrollFormIntoView, 30);
  };
  const prev = () => {
    setStep((s) => Math.max(1, s - 1));
    setTimeout(scrollFormIntoView, 30);
  };

  const submit = async () => {
    if (!form.credit_check_consent) { toast.error("Please agree to the soft eligibility check to submit."); return; }
    setSubmitting(true);
    try {
      // Coerce financial string values to numbers before hitting the API
      const payload = {
        ...form,
        annual_turnover: Number(form.annual_turnover) || 0,
        monthly_revenue: Number(form.monthly_revenue) || 0,
        existing_debt: Number(form.existing_debt) || 0,
      };
      const { data } = await api.post("/applications", payload);
      // Upload any attached documents (best-effort — errors surfaced but don't block success)
      for (const item of pendingDocs) {
        const fd = new FormData();
        fd.append("file", item.file);
        try {
          await api.post(`/applications/${data.id}/documents?category=${item.category}`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch {
          toast.error(`Couldn't upload ${item.file.name}`);
        }
      }
      setSuccess(data);
      // On mobile the sticky header changes on the success screen — start from the very top.
      setTimeout(() => window.scrollTo({ top: 0, behavior: "auto" }), 30);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const addFile = (file, category) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return toast.error("File exceeds 20MB limit.");
    setPendingDocs((d) => [...d, { file, category, id: Math.random().toString(36).slice(2) }]);
  };
  const removeFile = (id) => setPendingDocs((d) => d.filter((x) => x.id !== id));

  if (success) return <SuccessScreen app={success} />;

  return (
    <div className="bg-white text-ink min-h-screen">
      <Navbar />

      {/* Progress strip */}
      <div className="sticky top-[72px] z-30 bg-white/92 backdrop-blur-xl border-b border-line">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-4">
          <div className="flex items-center justify-between gap-2">
            {STEPS.map((s, i) => {
              const active = step === s.id;
              const done = step > s.id;
              return (
                <React.Fragment key={s.id}>
                  <div className="flex items-center gap-2.5 min-w-0" data-testid={`step-indicator-${s.id}`}>
                    <div className={`h-7 w-7 rounded-lg flex items-center justify-center text-[11px] font-extrabold transition-all shrink-0
                      ${done ? "bg-emerald text-white" : active ? "bg-ink text-white" : "bg-paper-warm text-ink/40"}`}>
                      {done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : String(s.id).padStart(2, "0")}
                    </div>
                    <span className={`text-[13px] font-bold hidden sm:block truncate ${active ? "text-ink" : done ? "text-ink/80" : "text-ink/40"}`}>
                      {s.label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`flex-1 h-[2px] min-w-[8px] rounded-full ${done ? "bg-emerald" : "bg-line"}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-12 md:py-16">
        <div className="grid lg:grid-cols-12 gap-10 md:gap-12">
          {/* Form column */}
          <div ref={formTopRef} className="lg:col-span-8 scroll-mt-[160px]">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-soft text-emerald-3 font-mono text-[11px] uppercase tracking-[0.12em] font-bold mb-5">
              Step {step} of {STEPS.length}
            </div>
            <h1 className="font-display font-extrabold text-[40px] md:text-[56px] leading-[0.98] tracking-[-0.035em] text-ink mb-4" data-testid="apply-step-title">
              {step === 1 && <>How much, <span className="text-emerald-2">how long?</span></>}
              {step === 2 && <>Tell us about <span className="text-emerald-2">the business.</span></>}
              {step === 3 && <>A quick look at <span className="text-emerald-2">the numbers.</span></>}
              {step === 4 && <>Who&apos;s the <span className="text-emerald-2">point of contact?</span></>}
              {step === 5 && <>One last look, <span className="text-emerald-2">then off we go.</span></>}
            </h1>
            <p className="text-ink/60 text-[16px] mb-10 max-w-lg leading-[1.55] font-medium">
              {step === 1 && "Slide to set the amount and term. Pick a funding purpose — one tap."}
              {step === 2 && "Company essentials. We only ask what we need to match you with the right lender."}
              {step === 3 && "Turnover and revenue drive the offer. Nothing is shared until you approve terms."}
              {step === 4 && "Who should we come back to with terms? Direct line — no call centres."}
              {step === 5 && "Double-check, tick the consents, and we'll route it to the panel."}
            </p>

            {step === 1 && (
              <div className="space-y-10">
                <div className="rounded-[24px] border border-line bg-paper-warm p-6 md:p-8">
                  <div className="flex items-baseline justify-between mb-5">
                    <div>
                      <div className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-ink/55 mb-2">Amount</div>
                      <div className="font-display font-extrabold text-[56px] md:text-[68px] leading-[0.95] tracking-[-0.04em] text-ink" data-testid="apply-amount-display">{formatGBPCompact(form.amount)}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-ink/55 mb-2">Indicative Monthly</div>
                      <div className="font-display font-extrabold text-[26px] md:text-[30px] tracking-[-0.03em] text-emerald-2">{formatGBP2(monthly)}</div>
                    </div>
                  </div>
                  <Slider
                    data-testid="apply-amount-slider"
                    value={[amountToPos(form.amount)]} min={0} max={SLIDER_STEPS} step={1}
                    onValueChange={(v) => set("amount", posToAmount(v[0]))}
                  />
                  <div className="flex justify-between font-mono text-[11px] text-ink/40 mt-2.5 font-semibold">
                    <span>£5k</span><span>£250k</span><span>£10M</span><span>£100M</span><span>£500M</span>
                  </div>

                  <div className="h-px bg-line my-7" />

                  <div className="flex items-baseline justify-between mb-3">
                    <div className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-ink/55">Term</div>
                    <div className="font-display font-extrabold text-xl tracking-[-0.02em]">{form.term_months} {form.term_months === 1 ? "month" : "months"}</div>
                  </div>
                  <Slider data-testid="apply-term-slider" value={[form.term_months]} min={1} max={120} step={1} onValueChange={(v) => set("term_months", v[0])} />
                  <div className="flex justify-between font-mono text-[11px] text-ink/40 mt-2.5 font-semibold">
                    <span>1m</span><span>36m</span><span>60m</span><span>120m</span>
                  </div>
                </div>

                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-ink/70 mb-4 block">What&apos;s the funding for?</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                    {PURPOSES.map((p) => (
                      <button
                        key={p} type="button" onClick={() => set("purpose", p)}
                        data-testid={`tile-purpose-${p.replace(/\s/g, "-").replace(/\//g, "-").toLowerCase()}`}
                        className={`rounded-xl px-4 py-3.5 text-left border transition-all font-bold text-[13.5px]
                          ${form.purpose === p ? "bg-ink text-white border-ink" : "bg-white border-line text-ink hover:border-ink/40 hover:bg-paper-warm"}`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-7">
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Company name" className="md:col-span-2"><Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Acme Trading Ltd" data-testid="input-company-name" className="h-12 rounded-xl font-medium" /></Field>
                  <Field label="Companies House number" hint="Optional">
                    <Input value={form.company_number} onChange={(e) => set("company_number", e.target.value)} placeholder="12345678" data-testid="input-company-number" className="h-12 rounded-xl font-medium" />
                  </Field>
                  <Field label="Business postcode"><Input value={form.business_postcode} onChange={(e) => set("business_postcode", e.target.value.toUpperCase())} placeholder="EC4N 7AF" data-testid="input-business-postcode" className="h-12 rounded-xl font-medium" /></Field>
                </div>

                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-ink/70 mb-3 block">Sector</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                    {SECTORS.map((s) => (
                      <button
                        key={s} type="button" onClick={() => set("sector", s)}
                        data-testid={`tile-sector-${s.replace(/\s/g, "-").replace(/&/g, "and").toLowerCase()}`}
                        className={`rounded-xl px-4 py-3 text-left border transition-all font-bold text-[13.5px]
                          ${form.sector === s ? "bg-ink text-white border-ink" : "bg-white border-line text-ink hover:border-ink/40 hover:bg-paper-warm"}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-ink/70 mb-3 block">Trading years</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                    {TRADING_YEARS.map((y) => (
                      <button
                        key={y} type="button" onClick={() => set("trading_years", y)}
                        data-testid={`tile-years-${y.replace(/\s/g, "-").replace(/[<+]/g, "").toLowerCase()}`}
                        className={`rounded-xl px-4 py-3 border transition-all font-bold text-[13px]
                          ${form.trading_years === y ? "bg-ink text-white border-ink" : "bg-white border-line text-ink hover:border-ink/40 hover:bg-paper-warm"}`}
                      >
                        {y}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-7">
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Annual turnover" hint="Last 12 months">
                    <Input type="number" min="0" inputMode="numeric" value={form.annual_turnover ?? ""} onChange={(e) => set("annual_turnover", e.target.value === "" ? "" : Number(e.target.value))} placeholder="£" data-testid="input-annual-turnover" className="h-12 rounded-xl font-medium" />
                  </Field>
                  <Field label="Monthly revenue" hint="Average, recent">
                    <Input type="number" min="0" inputMode="numeric" value={form.monthly_revenue ?? ""} onChange={(e) => set("monthly_revenue", e.target.value === "" ? "" : Number(e.target.value))} placeholder="£" data-testid="input-monthly-revenue" className="h-12 rounded-xl font-medium" />
                  </Field>
                  <Field label="Existing debt" hint="Total outstanding across all facilities">
                    <Input type="number" min="0" inputMode="numeric" value={form.existing_debt ?? ""} onChange={(e) => set("existing_debt", e.target.value === "" ? "" : Number(e.target.value))} placeholder="£ · Enter 0 if none" data-testid="input-existing-debt" className="h-12 rounded-xl font-medium" />
                  </Field>
                  <Field label="Business bank" hint="Optional">
                    <Input value={form.business_bank} onChange={(e) => set("business_bank", e.target.value)} placeholder="Barclays, HSBC, Starling…" data-testid="input-business-bank" className="h-12 rounded-xl font-medium" />
                  </Field>
                </div>
                <div className="flex items-start gap-3 rounded-xl bg-emerald-soft border border-emerald/25 p-4 text-[13px] text-emerald-3 leading-relaxed">
                  <Shield className="h-4 w-4 text-emerald-2 mt-0.5 shrink-0" strokeWidth={2.4} />
                  <span className="font-semibold text-emerald-3">Financials are used only to structure your offer. Nothing is shared with lenders until you approve terms.</span>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-7">
                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-ink/70 mb-3 block">Title</Label>
                  <div className="flex flex-wrap gap-2">
                    {TITLES.map((t) => (
                      <button key={t} type="button" onClick={() => set("title", t)}
                        data-testid={`tile-title-${t.toLowerCase()}`}
                        className={`min-w-[68px] h-11 rounded-full border text-[14px] font-bold px-5 transition-colors
                          ${form.title === t ? "bg-ink text-white border-ink" : "border-line text-ink hover:border-ink/40"}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="First name"><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="input-first-name" className="h-12 rounded-xl font-medium" /></Field>
                  <Field label="Last name"><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="input-last-name" className="h-12 rounded-xl font-medium" /></Field>
                  <Field label="Business email"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.co.uk" data-testid="input-email" className="h-12 rounded-xl font-medium" /></Field>
                  <Field label="Direct phone" hint="Mobile or landline"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="07xxx xxxxxx" data-testid="input-phone" className="h-12 rounded-xl font-medium" /></Field>
                </div>
                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-ink/70 mb-3 block">Your role</Label>
                  <div className="flex flex-wrap gap-2">
                    {ROLES.map((r) => (
                      <button key={r} type="button" onClick={() => set("role", r)}
                        data-testid={`tile-role-${r.replace(/\s/g, "-").toLowerCase()}`}
                        className={`h-11 rounded-full border text-[14px] font-bold px-5 transition-colors
                          ${form.role === r ? "bg-ink text-white border-ink" : "border-line text-ink hover:border-ink/40"}`}>
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="space-y-7">
                <div className="rounded-[24px] border border-line bg-paper-warm p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-ink/55 mb-5">Your application</div>
                  <div className="grid md:grid-cols-2 gap-x-10 gap-y-4 text-sm">
                    <RRow label="Amount" value={formatGBPCompact(form.amount)} />
                    <RRow label="Term" value={`${form.term_months} months`} />
                    <RRow label="Indicative monthly" value={formatGBP2(monthly)} />
                    <RRow label="Indicative total" value={formatGBP(total)} />
                    <RRow label="Purpose" value={form.purpose} />
                    <RRow label="Company" value={form.company_name} />
                    <RRow label="Companies House" value={form.company_number || "—"} />
                    <RRow label="Sector" value={form.sector} />
                    <RRow label="Trading years" value={form.trading_years} />
                    <RRow label="Business postcode" value={form.business_postcode} />
                    <RRow label="Annual turnover" value={formatGBP(form.annual_turnover)} />
                    <RRow label="Monthly revenue" value={formatGBP(form.monthly_revenue)} />
                    <RRow label="Existing debt" value={formatGBP(form.existing_debt)} />
                    <RRow label="Business bank" value={form.business_bank || "—"} />
                    <RRow label="Contact" value={`${form.title} ${form.first_name} ${form.last_name}`} />
                    <RRow label="Role" value={form.role} />
                    <RRow label="Email" value={form.email} />
                    <RRow label="Phone" value={form.phone} />
                  </div>
                </div>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer rounded-xl p-4 border border-line hover:border-ink/30 transition-colors" data-testid="consent-credit-check">
                    <Checkbox checked={form.credit_check_consent} onCheckedChange={(v) => set("credit_check_consent", Boolean(v))} className="mt-0.5" />
                    <span className="text-[14px] text-ink/80 leading-relaxed font-medium">
                      I agree to a soft eligibility check on the business. No footprint on the company or directors&apos; personal credit files. I confirm the information above is accurate.
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer rounded-xl p-4 border border-line hover:border-ink/30 transition-colors" data-testid="consent-marketing">
                    <Checkbox checked={form.marketing_consent} onCheckedChange={(v) => set("marketing_consent", Boolean(v))} className="mt-0.5" />
                    <span className="text-[14px] text-ink/65 leading-relaxed font-medium">
                      Keep me posted on SwiftCash news, market rate updates and product launches (optional).
                    </span>
                  </label>
                </div>

                {/* Supporting documents */}
                <div className="rounded-[20px] border border-line bg-paper-warm p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="font-display font-extrabold text-[17px] text-ink tracking-[-0.015em]">Supporting documents</div>
                      <div className="text-[13px] text-ink/60 font-medium mt-0.5">Optional — bank statements or filed accounts let us price faster. PDF/JPG/PNG · max 20MB each.</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <UploadBtn label="Bank statement" category="bank_statement" onAdd={addFile} testid="upload-bank" />
                    <UploadBtn label="Filed accounts" category="filed_accounts" onAdd={addFile} testid="upload-accounts" />
                    <UploadBtn label="Management accounts" category="management_accounts" onAdd={addFile} testid="upload-mgmt" />
                    <UploadBtn label="Other" category="other" onAdd={addFile} testid="upload-other" />
                  </div>
                  {pendingDocs.length > 0 && (
                    <ul className="space-y-2" data-testid="pending-docs">
                      {pendingDocs.map((d) => (
                        <li key={d.id} className="flex items-center justify-between gap-3 rounded-lg bg-white border border-line px-3 py-2">
                          <span className="text-[13px] text-ink font-semibold truncate flex-1">{d.file.name}</span>
                          <span className="font-mono text-[10.5px] text-ink/45 uppercase tracking-[0.1em] font-bold">{d.category.replace(/_/g, " ")}</span>
                          <button onClick={() => removeFile(d.id)} data-testid={`remove-doc-${d.id}`} className="text-ink/40 hover:text-coral">
                            <Trash2 className="h-4 w-4" strokeWidth={2.2} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            <div className="hidden md:flex items-center justify-between pt-12 mt-12 border-t border-line">
              <div className="flex items-center gap-3">
                {step > 1 ? (
                  <button onClick={prev} data-testid="step-back-btn" className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-line text-ink/80 hover:border-ink/40 hover:bg-paper-warm text-[14px] font-bold">
                    <ArrowLeft className="h-4 w-4" strokeWidth={2.4} /> Back
                  </button>
                ) : (
                  <Link to="/" data-testid="step-home-link" className="text-[13px] font-bold text-ink/55 hover:text-ink inline-flex items-center gap-1.5">
                    <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.4} /> Back to home
                  </Link>
                )}
                <button
                  onClick={openSaveDraft}
                  type="button"
                  data-testid="save-draft-btn"
                  className="hidden sm:inline-flex items-center gap-1.5 h-12 px-4 rounded-full border border-line text-ink/70 hover:border-emerald/50 hover:text-emerald text-[13px] font-bold"
                >
                  <Bookmark className="h-3.5 w-3.5" strokeWidth={2.4} /> Save &amp; continue later
                </button>
              </div>
              {step < STEPS.length ? (
                <button onClick={next} data-testid="step-next-btn" className="btn-shine group inline-flex items-center gap-2 h-12 px-7 rounded-full bg-ink text-white hover:bg-navy-2 text-[14px] font-extrabold transition-colors">
                  Continue <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.6} />
                </button>
              ) : (
                <button onClick={submit} disabled={submitting} data-testid="step-submit-btn" className="btn-shine group inline-flex items-center gap-2 h-12 px-7 rounded-full bg-emerald text-white hover:bg-emerald-2 text-[14px] font-extrabold disabled:opacity-50 shadow-emerald">
                  {submitting ? "Submitting…" : <>Submit application <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.6} /></>}
                </button>
              )}
            </div>

            {/* Spacer so the last field isn't obscured by the mobile sticky bar */}
            <div className="h-24 md:hidden" aria-hidden="true" />
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="sticky top-[148px] rounded-[24px] bg-ink text-white p-7 shadow-hero">
              <div className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-white/55 mb-3">Funding request</div>
              <div className="font-display font-extrabold text-[52px] md:text-[60px] leading-[0.95] tracking-[-0.04em] mb-2">{formatGBPCompact(form.amount)}</div>
              <div className="font-mono text-[11px] uppercase tracking-[0.1em] text-white/55 mb-7 font-bold">
                over {form.term_months} {form.term_months === 1 ? "month" : "months"} · {form.purpose}
              </div>

              <div className="space-y-4 pb-6 border-b border-white/15">
                <KV label="Indicative monthly" value={formatGBP2(monthly)} />
                <KV label="Indicative APR" value={`${(DEFAULT_INDICATIVE_APR * 100).toFixed(1)}%`} />
                <KV label="Indicative total" value={formatGBP(total)} emphasis />
              </div>

              <div className="mt-6 p-3 rounded-xl bg-emerald/10 border border-emerald/25 text-[12px] text-white/80 flex items-start gap-2 font-medium">
                <Shield className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald" strokeWidth={2.4} />
                Encrypted · soft eligibility check · never shared without consent.
              </div>

              <div className="mt-4 text-[11px] text-white/45 leading-relaxed">
                Illustrative only. Final rate is bespoke and depends on turnover, security, sector and trading history. Rates from 5.9% APR.
              </div>
            </div>
          </aside>
        </div>
      </div>
      <Footer />

      {/* Mobile sticky action bar — Continue / Submit always one tap away */}
      <div className="md:hidden fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 backdrop-blur-md" data-testid="mobile-sticky-cta"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}>
        <div className="px-4 py-3 flex items-center gap-2.5">
          {step > 1 ? (
            <button
              onClick={prev}
              data-testid="mobile-back-btn"
              aria-label="Back to previous step"
              className="inline-flex items-center justify-center h-12 w-12 rounded-full border border-line text-ink/80 shrink-0 active:scale-95"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={2.4} />
            </button>
          ) : null}
          <button
            onClick={openSaveDraft}
            type="button"
            data-testid="mobile-save-draft-btn"
            aria-label="Save and continue later"
            className="inline-flex items-center justify-center h-12 w-12 rounded-full border border-line text-ink/70 shrink-0 active:scale-95"
          >
            <Bookmark className="h-4 w-4" strokeWidth={2.4} />
          </button>
          {step < STEPS.length ? (
            <button
              onClick={next}
              data-testid="mobile-next-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-full bg-ink text-white font-extrabold text-[14px] shadow-hero active:scale-[0.99]"
            >
              Continue <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="mobile-submit-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-full bg-emerald text-white font-extrabold text-[14px] disabled:opacity-50 shadow-emerald active:scale-[0.99]"
            >
              {submitting ? "Submitting…" : (<>Submit <ArrowRight className="h-4 w-4" strokeWidth={2.6} /></>)}
            </button>
          )}
        </div>
      </div>

      {/* Save & Resume modal */}
      <Dialog open={saveOpen} onOpenChange={(o) => !o && closeSaveDraft()}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md bg-white border-line rounded-[24px] p-0 overflow-hidden" data-testid="save-draft-modal">
          <VisuallyHidden>
            <DialogTitle>Save your application</DialogTitle>
            <DialogDescription>We&rsquo;ll email you a private link to resume where you left off.</DialogDescription>
          </VisuallyHidden>
          <div className="bg-navy text-white p-7 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald/15 border border-emerald/30 text-emerald font-mono text-[10.5px] uppercase tracking-[0.14em] font-bold mb-4">
                <Bookmark className="h-3 w-3" strokeWidth={2.6} /> Save &amp; resume
              </div>
              <h2 className="font-display font-extrabold text-[24px] tracking-[-0.03em] leading-[1.15] text-white mb-2">
                {saveSent ? "Check your inbox." : "Pick this up later — no lost progress."}
              </h2>
              <p className="text-[13.5px] text-white/70 font-medium leading-[1.55]">
                {saveSent
                  ? `We've sent a secure resume link to ${saveEmail}. It expires in 7 days.`
                  : "We'll email you a private link that jumps straight back to this exact spot. Link expires in 7 days."}
              </p>
            </div>
          </div>
          {saveSent ? (
            <div className="p-7 space-y-4">
              <div className="rounded-xl bg-emerald-soft/60 border border-emerald/20 p-4 flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-emerald text-white flex items-center justify-center shrink-0">
                  <Mail className="h-4 w-4" strokeWidth={2.6} />
                </div>
                <div>
                  <div className="font-display font-extrabold text-[14px] text-navy">Email on its way</div>
                  <div className="text-[12.5px] text-navy/70 mt-0.5 font-medium">Nothing has been shared with lenders — you&rsquo;re in control.</div>
                </div>
              </div>
              <button
                onClick={closeSaveDraft}
                data-testid="save-draft-done"
                className="w-full h-12 rounded-xl bg-ink text-white font-extrabold text-[14px] hover:bg-navy-2"
              >
                Keep going
              </button>
            </div>
          ) : (
            <div className="p-7 space-y-4">
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-[0.12em] font-bold text-ink/60 mb-2">Where should we send the link?</label>
                <Input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={saveEmail}
                  onChange={(e) => setSaveEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveDraft(); }}
                  placeholder="you@company.co.uk"
                  data-testid="save-draft-email"
                  className="h-12 rounded-xl font-medium"
                />
              </div>
              <div className="flex gap-2.5">
                <button
                  onClick={closeSaveDraft}
                  type="button"
                  className="inline-flex items-center justify-center h-12 px-5 rounded-xl border border-line text-ink/70 font-bold text-[13.5px] hover:bg-paper-warm"
                >
                  Cancel
                </button>
                <button
                  onClick={saveDraft}
                  disabled={saving}
                  data-testid="save-draft-submit"
                  className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-emerald text-white font-extrabold text-[14px] hover:bg-emerald-2 disabled:opacity-50 shadow-emerald"
                >
                  {saving ? "Sending…" : (<>Email me the link <ArrowRight className="h-4 w-4" strokeWidth={2.6} /></>)}
                </button>
              </div>
              <p className="text-[11px] text-ink/45 font-mono uppercase tracking-[0.1em] font-bold">
                We only use this email to send you the resume link · GDPR safe
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, hint, children, className = "" }) {
  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <Label className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-ink/60">{label}</Label>
        {hint && <span className="font-mono text-[10.5px] text-ink/35">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
function KV({ label, value, emphasis }) {
  return (
    <div className="flex items-center justify-between">
      <span className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/55">{label}</span>
      <span className={`font-display font-extrabold ${emphasis ? "text-emerald text-[22px]" : "text-white text-[17px]"} leading-none tracking-[-0.02em]`}>{value}</span>
    </div>
  );
}
function RRow({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-ink/50 mb-1">{label}</div>
      <div className="text-ink font-semibold break-words">{value || "—"}</div>
    </div>
  );
}
function UploadBtn({ label, category, onAdd, testid }) {
  const ref = React.useRef(null);
  return (
    <>
      <input type="file" ref={ref} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xlsx,.xls,.csv"
        onChange={(e) => { onAdd(e.target.files?.[0], category); e.target.value = ""; }}
        data-testid={`${testid}-input`} />
      <button type="button" onClick={() => ref.current?.click()} data-testid={testid}
        className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-line bg-white hover:border-emerald/40 hover:bg-emerald-soft text-ink font-bold text-[13px]">
        <Upload className="h-3.5 w-3.5" strokeWidth={2.4} /> {label}
      </button>
    </>
  );
}

function SuccessScreen({ app }) {
  useEffect(() => {
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  return (
    <div className="bg-white text-ink min-h-screen">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-20 md:py-28 text-center">
        <div className="inline-flex h-20 w-20 rounded-2xl bg-emerald text-white items-center justify-center mb-8 shadow-emerald">
          <CheckCircle2 className="h-10 w-10" strokeWidth={2.4} />
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-soft text-emerald-3 font-mono text-[11px] uppercase tracking-[0.12em] font-bold mb-5">
          Application received
        </div>
        <h1 className="font-display font-extrabold text-[48px] md:text-[72px] leading-[0.95] tracking-[-0.04em] text-ink mb-6" data-testid="apply-success-title">
          Routed to <span className="text-emerald-2">the panel</span>.
        </h1>
        <p className="text-[17px] text-ink/70 max-w-xl mx-auto mb-12 leading-relaxed font-medium">
          Thanks, {app.first_name}. We&apos;ve got the application for{" "}
          <b className="text-ink">{formatGBPCompact(app.amount)}</b> over <b className="text-ink">{app.term_months} {app.term_months === 1 ? "month" : "months"}</b> for <b className="text-ink">{app.company_name}</b>. Our team will come back to <b className="text-ink">{app.email}</b> with indicative terms shortly.
        </p>
        <div className="rounded-2xl bg-paper-warm border border-line p-6 max-w-md mx-auto text-left mb-10">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-ink/55 mb-2">Your reference</div>
          <div className="font-mono text-[14px] text-ink break-all" data-testid="apply-success-ref">{app.id}</div>
        </div>
        <Link to="/" data-testid="apply-success-home-btn" className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-ink text-white hover:bg-navy-2 transition-colors text-[14px] font-extrabold">
          Back to home <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
        </Link>
      </div>
      <Footer />
    </div>
  );
}
